/**
 * Pure helpers for the project chat transcript renderer. Lives in a
 * sibling file so unit tests can import them without dragging in the
 * full timeline presentation (markdown, design system, computer-use
 * task block + its agent-client deps).
 */

import type { ChatItem } from '../../../contracts/harness.types';
import { prepareRefTagsForRender } from '../../../contracts/projects/ref-tags';
import { displayBodyFromAddRoutineChatSeedPrompt } from '../../../contracts/workflows/lib/build-add-routine-chat-seed-prompt';
import { displayBodyFromCreateRoutineWirePrompt } from '../../../contracts/workflows/lib/createRoutineWithHeidi';
import { WORKFLOW_CONTEXT_USER_REQUEST_MARKER } from '../../../contracts/workflows/lib/workflow-context-marker';
import { rewriteOutsideMarkdownFences } from '../../markdown-fences';
import {
  isMakeAsWorkflowUserText,
  isUpdateWorkflowUserText,
  MAKE_AS_WORKFLOW_DISPLAY_LABEL,
  UPDATE_WORKFLOW_DISPLAY_LABEL,
} from '../../workflows/makeAsWorkflowPrompt';
import { TASK_LAUNCH_INSTRUCTION } from '../work-home-tasks';

/** Preamble kinds `serializeLinkedContext` emits for template and library-document chips. */
const OBJECT_PREAMBLE_KINDS = [
  'template',
  'form_template',
  'skill_file',
  'upload',
  'work_output',
] as const;

type ObjectPreambleKind = (typeof OBJECT_PREAMBLE_KINDS)[number];

/**
 * Parsed shape of a user-message text block whose composer prepended
 * one or more linked-context lines (``@session: …`` / ``@patient: …``
 * / ``@task: …`` / ``@template: …`` / a library document).
 *
 * The renderer surfaces task, template, and file ``preamble`` items as
 * compact chips above the actual ``body`` so the user doesn't see raw
 * ``[id=…]`` strings in their own message. Session and patient items stay
 * in the parse — the prompt still carries them — but the composer context
 * card is their only clinician-facing home. The full text still flows to
 * the agent — this is a render-only split.
 */
export interface UserMessagePreambleItem {
  kind: 'session' | 'patient' | 'task' | ObjectPreambleKind;
  /** Display label — session, patient, template or document name. */
  name: string;
  /** Secondary line, e.g. patient name on a ``@session`` preamble. */
  subtitle?: string;
  /** The ``[id=…]`` from the preamble (session id / patient profile
   *  id), when present — lets the rendered chip open the matching
   *  artifact in the canvas on click. */
  id?: string;
}

export interface ParsedUserMessage {
  preamble: UserMessagePreambleItem[];
  body: string;
  /**
   * Routine-run opening turn — render as a compact card (title only) instead
   * of the agent seed brief. Set when the wire text is a short ``Run {name}``
   * label (post-CONTEXT strip) or a legacy long instructions seed.
   */
  routineRunTitle?: string;
  /**
   * Exact ``/slug`` skill invoke in chat. The timeline looks the slug up and
   * renders the same ``Ran routine`` outcome row as a run-started seed.
   */
  slashRoutineSlug?: string;
}

export interface ParseUserPreambleOptions {
  /**
   * Project title used to label legacy workflow-run seeds that still persist
   * the full instructions as the user message (pre CONTEXT-wrapper backend).
   */
  routineRunTitleFallback?: string;
  /**
   * True only for the chat's opening user message — the sole position a
   * backend-synthesized ``Run {name}`` seed (``_build_seed_message``) can
   * ever occupy. ``ROUTINE_RUN_LABEL_RE`` is checked ONLY when this is true:
   * without the guard, an ordinary clinician message that happens to start
   * with the literal word "Run" (e.g. "Run the diabetes protocol for this
   * patient") matches the same shape and silently renders no bubble at all.
   *
   * KNOWN RESIDUAL EDGE CASE (AGE-2241): position is a proxy, not proof —
   * this guarantees the seed is caught (it can only ever be first), but not
   * the reverse. A clinician whose GENUINE opening message in a brand-new
   * chat happens to start with "Run " (e.g. starting a chat with "Run the
   * diabetes protocol for this patient") still hits the exact bug this
   * option was added to fix, just narrowed from "anywhere" to "only the
   * first message." The FE has no signal to tell the two apart from text
   * alone; closing this fully needs a backend-supplied marker on the seed
   * item itself (tracked in AGE-2241) rather than an inferred one here.
   */
  isRoutineRunSeed?: boolean;
}

/**
 * Seed from the split-view configure / try-run chat:
 * ``Context: the user is viewing their workflow "Name" (workflow_id: …).``
 * ``Context: the user is viewing their routine "Name" (workflow_id: …).``
 * ``Context: the user is trying the routine "Name" (workflow_id: …).``
 * ``Context: the user is exploring "Name", a Heidi preset from the routines
 *   catalog. …`` (catalog preset, not yet in the library — no workflow_id).
 */
const WORKFLOW_VIEWING_CONTEXT_RE =
  /^Context:\s*the user is (?:viewing their (?:workflow|routine)|trying the routine|exploring)\s+"([^"]+)"/i;

/**
 * Routine-pane composer turns prepend ``buildWorkflowContext`` to the user's
 * text. That preamble ends with ``WORKFLOW_CONTEXT_USER_REQUEST_MARKER`` so
 * the bubble can recover only the clinician's words. A context-only run seed
 * (marker with nothing after it) keeps the existing title-only treatment.
 */
const WORKFLOW_CONTEXT_USER_REQUEST_NEEDLE = `\n${WORKFLOW_CONTEXT_USER_REQUEST_MARKER}\n`;

const workflowContextUserRequest = (body: string): string => {
  const markerAt = body.lastIndexOf(WORKFLOW_CONTEXT_USER_REQUEST_NEEDLE);
  if (markerAt < 0) return '';
  return body.slice(markerAt + WORKFLOW_CONTEXT_USER_REQUEST_NEEDLE.length).trim();
};

/**
 * Long instructed-workflow seeds (full agent brief as the first user bubble).
 * Used as a fallback until_dev ships the CONTEXT-wrapped seed format.
 */
export const isLikelyWorkflowRunSeedText = (text: string): boolean => {
  const t = text.trim();
  if (t.length < 160) return false;
  // Recorded workflows seed a short slash command — leave those alone.
  if (t.startsWith('/')) return false;
  // Agent briefs often open with "You are…" / "You're a…" after a short lead-in
  // (e.g. "Improve my templates…\n\nYou're a skilled LLM…").
  // Split-view configure chat seeds use the Viewing-context line instead.
  return (
    WORKFLOW_VIEWING_CONTEXT_RE.test(t) ||
    /\(.*workflow_id:\s*[\w-]+\)/i.test(t) ||
    /\bYou(?:(?:'|’)re| are| will)\b/i.test(t) ||
    /\bFollow these steps\b/i.test(t) ||
    /\bClinician setup:\b/i.test(t) ||
    /\bSTEP\s*\d+\s*:/i.test(t) ||
    /\bclinical document template\b/i.test(t) ||
    /\b\[placeholder\]\b/i.test(t) ||
    /\bAI_instructions\b/i.test(t) ||
    /\n#{1,3}\s+\S/.test(t)
  );
};

/** Short clinician-facing label produced by ``_build_seed_message``. */
// DotAll without the ``s`` flag (tsc target predates es2018).
const ROUTINE_RUN_LABEL_RE = /^Run\s+([\s\S]+)$/;

/** A composer skill invoke that is only ``/slug`` with no surrounding prose. */
const SLASH_ROUTINE_RE = /^\/([a-z0-9]+(?:-[a-z0-9]+)*)$/i;

/**
 * Matches forged or backend-wrapped ``<trusted_*>`` / ``<untrusted_*>``
 * boundary tags. Mirrors ``pkg/agent_harness/core/trust_framing.py`` so
 * LLM plumbing never surfaces in the clinician transcript.
 */
const TRUST_BOUNDARY_TAG_RE = /<\/?(?:trusted|untrusted)_[^>]*>/gi;

/** Workflow dispatch seeds from ``service/workflows/dispatch.py`` */
const UNTRUSTED_WORKFLOW_SEED_RE =
  /<untrusted_(?:workflow_instructions|clinician_setup|artifact_refs)>/i;

/**
 * Strip trust-boundary XML tags from text destined for the UI. Tags and
 * their delimiters are removed; inner payload text is kept so a mixed
 * user message still reads naturally after cleanup.
 */
export const stripTrustBoundaryTags = (text: string | undefined): string => {
  if (!text) return '';
  return text.replace(TRUST_BOUNDARY_TAG_RE, '').trim();
};

/** Whether ``text`` is a workflow-run seed wrapped in ``<untrusted_*>`` blocks. */
export const isUntrustedWorkflowRunSeedText = (text: string): boolean =>
  UNTRUSTED_WORKFLOW_SEED_RE.test(text);

/**
 * Pull leading ``@session:`` / ``@patient:`` / ``@task:`` lines out of
 * a user message text block and return them as structured chips + the
 * remaining body. Matches the format produced by
 * ``serializeLinkedContext`` / ``buildTaskLaunchPromptPreamble``:
 *
 *   @session: <name> (<patient>) [id=<sid>]
 *   @patient: <name> [id=<pid>]
 *   @task: <title> [id=<tid>]
 *
 *   <body…>
 *
 * Tolerates the optional ``(<patient>)`` and ``[id=…]`` segments
 * being absent so a future change to the serializer doesn't silently
 * break the chip view. Blank lines between preamble entries are
 * allowed; the first non-preamble non-blank line starts the body.
 */
export const parseUserPreamble = (
  text: string | undefined,
  options: ParseUserPreambleOptions = {}
): ParsedUserMessage => {
  const raw = text ?? '';
  const hadUntrustedWorkflowSeed = isUntrustedWorkflowRunSeedText(raw);
  const internalInstructionsStripped = _stripInternalInstructions(
    _stripContextBlock(raw)
  );
  const addRoutineChatSeedDisplay = options.isRoutineRunSeed
    ? displayBodyFromAddRoutineChatSeedPrompt(internalInstructionsStripped.trim())
    : null;
  const lines = _stripInternalInstructions(
    stripTrustBoundaryTags(_stripContextBlock(raw))
  ).split('\n');
  const preamble: UserMessagePreambleItem[] = [];
  let bodyStart = 0;
  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i] ?? '';
    const trimmed = raw.trim();
    if (!trimmed) {
      bodyStart = i + 1;
      continue;
    }
    const sessionRest = _stripPrefix(trimmed, '@session:');
    if (sessionRest !== null) {
      preamble.push(_parseSessionPreamble(sessionRest));
      bodyStart = i + 1;
      continue;
    }
    const patientRest = _stripPrefix(trimmed, '@patient:');
    if (patientRest !== null) {
      preamble.push({
        kind: 'patient',
        name: _stripIdSuffix(patientRest).trim() || 'Patient',
        id: _extractId(patientRest),
      });
      bodyStart = i + 1;
      continue;
    }
    const taskRest = _stripPrefix(trimmed, '@task:');
    if (taskRest !== null) {
      preamble.push({
        kind: 'task',
        name: _stripIdSuffix(taskRest).trim() || 'Task',
        id: _extractId(taskRest),
      });
      bodyStart = i + 1;
      continue;
    }
    const objectItem = _parseObjectPreamble(trimmed);
    if (objectItem) {
      preamble.push(objectItem);
      bodyStart = i + 1;
      continue;
    }
    break;
  }
  const body = lines.slice(bodyStart).join('\n').trim();
  if (isUpdateWorkflowUserText(body)) {
    return {
      preamble,
      body: UPDATE_WORKFLOW_DISPLAY_LABEL,
    };
  }
  if (isMakeAsWorkflowUserText(body)) {
    return {
      preamble,
      body: MAKE_AS_WORKFLOW_DISPLAY_LABEL,
    };
  }
  const runLabelMatch = options.isRoutineRunSeed
    ? body.match(ROUTINE_RUN_LABEL_RE)
    : null;
  if (runLabelMatch?.[1]?.trim()) {
    return {
      preamble,
      body: '',
      routineRunTitle: runLabelMatch[1].trim(),
    };
  }
  const slashMatch = body.match(SLASH_ROUTINE_RE);
  if (slashMatch?.[1]) {
    return {
      preamble,
      body: '',
      slashRoutineSlug: slashMatch[1],
    };
  }
  if (addRoutineChatSeedDisplay !== null) {
    return { preamble, body: addRoutineChatSeedDisplay };
  }
  // Configure-pane seeds carry the routine name on the Context: line — prefer
  // that over dumping the agent brief into the bubble.
  // First-turn position is only a mitigation: full authentication needs a
  // backend-marked synthetic-message type rather than user-copyable text.
  const viewingMatch = options.isRoutineRunSeed
    ? body.match(WORKFLOW_VIEWING_CONTEXT_RE)
    : null;
  if (viewingMatch?.[1]?.trim()) {
    const userRequest = workflowContextUserRequest(body);
    if (userRequest) {
      return { preamble, body: userRequest };
    }
    return {
      preamble,
      body: '',
      routineRunTitle: viewingMatch[1].trim(),
    };
  }
  const createRoutineDisplay = displayBodyFromCreateRoutineWirePrompt(body);
  if (createRoutineDisplay !== null) {
    return { preamble, body: createRoutineDisplay };
  }
  // A routine opened with extra composer text (`buildWorkflowTurnWireText`)
  // shows the clinician's words, not the brief they were appended to. Opening
  // message only, like the other seed branches, so a later message cannot
  // hide its own text behind a copied marker.
  const routineUserRequest = options.isRoutineRunSeed
    ? workflowContextUserRequest(body)
    : '';
  if (
    routineUserRequest &&
    (hadUntrustedWorkflowSeed || isLikelyWorkflowRunSeedText(body))
  ) {
    return {
      preamble,
      body: routineUserRequest,
      ...(options.routineRunTitleFallback && {
        routineRunTitle: options.routineRunTitleFallback,
      }),
    };
  }
  if (
    options.routineRunTitleFallback &&
    (hadUntrustedWorkflowSeed || isLikelyWorkflowRunSeedText(body))
  ) {
    return {
      preamble,
      body: '',
      routineRunTitle: options.routineRunTitleFallback,
    };
  }
  // Belt-and-braces: never surface leftover boundary markup in a bubble.
  return { preamble, body: stripTrustBoundaryTags(body) };
};

/**
 * Queue rows show the clinician's words, not the ``@session`` / ``[CONTEXT]``
 * wire the turn still carries to the model.
 */
export const queuedTurnPreviewText = (previewText: string): string => {
  const { body, routineRunTitle } = parseUserPreamble(previewText);
  const visible = (routineRunTitle ?? body).trim();
  return visible || previewText.trim();
};

/**
 * Session and patient context is the composer's "Working with" card.
 * Those preamble lines still ride the prompt; they are not a bubble.
 */
export const isComposerContextPreamble = (item: UserMessagePreambleItem): boolean =>
  item.kind === 'session' || item.kind === 'patient';

/**
 * Whether a ``user_message`` renders a visible ``UserBubble``. A message
 * with no body, no task/template/file preamble chips, and no attachments
 * produces no DOM. A session or patient preamble alone does not.
 *
 * Single source of truth shared by ``UserBubble`` (its null-render guard)
 * and ``ProjectTurnTimeline`` (which only opens a new turn group for a
 * visible user message) so the two can't drift. If they diverged, an empty
 * synthetic message could open a phantom turn group — the empty-band
 * spacing bug — or a visible message could be silently folded away. Takes a
 * pre-parsed message so callers that already parsed don't pay for a second
 * pass.
 */
export const userMessageRendersBubble = (
  parsed: ParsedUserMessage,
  attachmentCount: number
): boolean =>
  Boolean(parsed.body) ||
  Boolean(parsed.routineRunTitle) ||
  Boolean(parsed.slashRoutineSlug) ||
  parsed.preamble.some((item) => !isComposerContextPreamble(item)) ||
  attachmentCount > 0;

/**
 * Whether a timeline item is a real, content-bearing ``user_message`` — the
 * item-level form of ``userMessageRendersBubble``. The orchestrator also
 * posts synthetic empty user messages mid-turn (feeding tool results back to
 * the agent, continuation prompts); those render no bubble and must never
 * count as a turn/task boundary. Shared by ``ProjectTurnTimeline`` (turn
 * grouping) and the computer-use trace mapper (current-task scoping) so the
 * boundary rule can't drift between surfaces.
 *
 * ``isRoutineRunSeed`` must be true only when ``item`` is the chat's opening
 * user message (see ``ParseUserPreambleOptions.isRoutineRunSeed``) — every
 * caller is responsible for computing that itself, since this function sees
 * one item at a time and has no notion of the item's position.
 */
export const isVisibleUserMessage = (item: ChatItem, isRoutineRunSeed = false): boolean =>
  item.kind === 'user_message' &&
  userMessageRendersBubble(
    parseUserPreamble(item.text, { isRoutineRunSeed }),
    item.attachments?.length ?? 0
  );

/**
 * Drop a leading ``[CONTEXT] … [/CONTEXT]`` block from the user-message
 * text. The Projects backend prepends this machine-readable block to the
 * turn so the parent agent can forward structured ids
 * (``@attachment_ids``, ``@session_id``, ``@jurisdiction``, …) into
 * subagent tool calls — see ``service/projects/project_service.py::
 * _serialize_subagent_context``. It's plumbing for the LLM, never
 * something the clinician should see in their own bubble (e.g. uploading
 * a file would otherwise surface a raw ``@attachment_ids: <id>`` line
 * instead of just the file chip).
 *
 * Render-only: the full text still reaches the agent — we only hide the
 * block from the transcript. Non-greedy so it stops at the first
 * ``[/CONTEXT]`` and leaves any following ``@session:`` / ``@patient:``
 * chip preamble intact for the parser below.
 */
const _stripContextBlock = (text: string): string => {
  const match = text.match(/^\s*\[CONTEXT\][\s\S]*?\[\/CONTEXT\]\s*/);
  return match ? text.slice(match[0].length) : text;
};

const SELECTED_SKILL_INTERNAL_INSTRUCTION =
  'Run the selected computer-use skill now by calling run_computer_use_task. Do not ask the user to repeat the skill; the selected skill instructions are attached server-side.';

const _stripInternalInstructions = (text: string): string =>
  text
    .replace(SELECTED_SKILL_INTERNAL_INSTRUCTION, '')
    .replace(TASK_LAUNCH_INSTRUCTION, '')
    .trim();

const _stripPrefix = (line: string, prefix: string): string | null => {
  if (!line.startsWith(prefix)) return null;
  return line.slice(prefix.length).trim();
};

const _stripIdSuffix = (s: string): string =>
  s.replace(/\s*\[id=[^\]]+\]\s*$/, '').trim();

const _parseObjectPreamble = (line: string): UserMessagePreambleItem | null => {
  for (const kind of OBJECT_PREAMBLE_KINDS) {
    const rest = _stripPrefix(line, `@${kind}:`);
    if (rest !== null) return { kind, name: _stripIdSuffix(rest), id: _extractId(rest) };
  }
  return null;
};

const _extractId = (s: string): string | undefined => {
  const m = s.match(/\[id=([^\]]+)\]/);
  return m?.[1]?.trim() || undefined;
};

const _parseSessionPreamble = (rest: string): UserMessagePreambleItem => {
  const id = _extractId(rest);
  const withoutId = _stripIdSuffix(rest);
  // ``Name (Patient)`` → name="Name", subtitle="Patient".
  const m = withoutId.match(/^(.+?)\s+\(([^)]+)\)\s*$/);
  if (m && m[1] && m[2]) {
    return { kind: 'session', name: m[1].trim(), subtitle: m[2].trim(), id };
  }
  return { kind: 'session', name: withoutId.trim() || 'Session', id };
};

/**
 * Recoverable "I need to know which patient/session" tool failures,
 * plus tool failures written for the model or backend rather than the
 * clinician. A destructive red banner for either is noise.
 */
export const isRecoverableContextPrompt = (raw: string | undefined): boolean => {
  const text = raw?.toLowerCase().trim();
  if (!text) return false;
  return (
    text.includes('patient profile could not be resolved') ||
    text.includes('no patient profile found for the provided ehr patient id') ||
    text.includes('choose a note template to continue') ||
    /\bis required\b/.test(text) ||
    text.includes('is not available in this run') ||
    text.includes('do not call it again') ||
    text.includes('available tools:') ||
    text.includes("not available in this conversation's scope") ||
    text.includes('is not a connected-app tool') ||
    text.includes('call integrations.apps.list') ||
    text.includes('call it through') ||
    text.includes('the user was not shown this question') ||
    text.includes('do not call this tool again') ||
    text.includes('arguments must be an object') ||
    text.includes('was rejected by the user') ||
    text.includes('do not retry it') ||
    text.includes('<user_message>') ||
    text.includes("treat the feedback as the user's current instruction")
  );
};

/**
 * Strip raw machine identifiers from agent prose before rendering —
 * 24-char Mongo ObjectIds, optionally preceded by a short
 * "Session ID:" / "MRN:" style label and/or wrapped in backticks.
 * They're meaningless to clinicians (the inline cards already provide
 * an "Open" affordance for anything viewable). This is a belt-and-
 * braces complement to the agent prompt rule; the 24-hex pattern is
 * specific enough that real clinical prose effectively never trips it,
 * and we short-circuit when no such token is present.
 *
 * Do **not** strip a 24-hex run that is a substring of a longer
 * ``[a-f0-9]`` token. Heidi session ids are ~37-digit decimal strings;
 * treating the first 24 digits as an ObjectId left clinicians with a
 * truncated suffix (IA still saw the full id in the raw transcript).
 *
 * Do **not** strip ObjectIds that are structural (``data-id="…"`` /
 * ``data-id='…'`` on cite tags, ``/projects/<id>`` URL paths). A bare
 * strip emptied those and made chips fall back to plain text + clipped
 * project links on scribe.dev.
 *
 * Tradeoff: an ObjectId immediately after ``"`` / ``'`` / ``/`` is kept
 * (covers attrs + URL paths; also keeps rare prose ``"64f0…"``). ``=`` is
 * *not* structural so ``MRN=<hex>`` still strips. Implemented procedurally
 * (not one mega-regex) so Sonar stays happy on complexity / ReDoS.
 */
const _OBJECT_ID_HEX = /[a-f0-9]{24}/gi;
const _HEX_CHAR = /[a-f0-9]/i;
const _LABEL_SEP = /[\s:=–—-]/;
const _LABEL_WORD = /(?:IDs?|MRN)$/i;
const _ALPHA_WORD = /[A-Za-z]+$/;

/** Expand left over ``Session ID: `` / ``MRN=`` style labels before an id. */
const expandMachineIdLabelPrefix = (text: string, idStart: number): number => {
  let i = idStart;
  while (i > 0 && _LABEL_SEP.test(text[i - 1] ?? '')) i -= 1;
  const word = text.slice(0, i).match(_LABEL_WORD);
  if (!word) return idStart;
  i -= word[0].length;
  for (let n = 0; n < 3; n += 1) {
    if (i === 0 || text[i - 1] !== ' ') break;
    const prior = text.slice(0, i - 1).match(_ALPHA_WORD);
    if (!prior) break;
    i = i - 1 - prior[0].length;
  }
  return i;
};

/** Optional ``. `` / ``, `` immediately before a labelled / bare id clause. */
const expandLeadingClausePunct = (text: string, from: number): number => {
  let i = from;
  while (i > 0 && text[i - 1] === ' ') i -= 1;
  if (i > 0 && (text[i - 1] === '.' || text[i - 1] === ',')) i -= 1;
  return i;
};

/** Complete cite pairs — ``<project data-id>``, legacy ``<chat>``, normalized ``<ref>``. */
const _COMPLETE_CITE_TAG =
  /<(?:project|chat)\b[^>]*>[\s\S]*?<\/(?:project|chat)>|<ref\b[^>]*>[^<]*<\/ref>/gi;
/**
 * Mid-stream incomplete cite markup (Evidence strips dangling ``<cite>`` the
 * same way). Covers an unclosed opening tag and an opened-but-unclosed element.
 */
const _DANGLING_PROJECT_CITE_TAG =
  /<(?:project|chat)\b[^>]*$|<(?:project|chat)\b[^>]*>(?:(?!<\/(?:project|chat)>)[\s\S])*$/i;
const _CITE_TAG_PLACEHOLDER = /\0CITETAG(\d+)\0/g;

/**
 * Tag-*name* still being typed (`<`, `<p`, … `<projec`, `<cha`). The dangling
 * regex above only matches once ``project``/``chat`` is complete — without this,
 * those prefixes paint as raw text at the caret (visible on scribe.dev).
 * String prefix check (not a nested optional regex) keeps complexity low.
 */
const stripProjectCiteTagNamePrefix = (text: string): string => {
  const lt = text.lastIndexOf('<');
  if (lt < 0) return text;
  const name = text.slice(lt + 1);
  // Not a bare tag-name stub (attrs / whitespace / close already started).
  if (name.includes('>') || /\s/.test(name)) return text;
  const lower = name.toLowerCase();
  if (lower.length === 0 || 'project'.startsWith(lower) || 'chat'.startsWith(lower)) {
    return text.slice(0, lt);
  }
  return text;
};

/** Drop incomplete cite markup that would otherwise paint as raw HTML. */
const stripIncompleteProjectCiteMarkup = (text: string): string =>
  stripProjectCiteTagNamePrefix(text.replace(_DANGLING_PROJECT_CITE_TAG, ''));
/**
 * Settled transcript with a broken opener: keep any title text after ``>``,
 * never restore the raw ``<project …>`` (that was the flash users saw when
 * ``streaming`` flipped false mid-tag).
 */
const unwrapDanglingProjectCiteMarkup = (text: string): string =>
  text.replace(_DANGLING_PROJECT_CITE_TAG, (match) => {
    const opened = /^<(?:project|chat)\b[^>]*>([\s\S]*)$/i.exec(match);
    return opened?.[1] ?? '';
  });

const stripMachineIdsFromProse = (text: string, trimEnd: boolean): string => {
  if (!/[a-f0-9]{24}/i.test(text)) return trimEnd ? text.trimEnd() : text;

  const removals: Array<{ from: number; to: number }> = [];
  _OBJECT_ID_HEX.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = _OBJECT_ID_HEX.exec(text)) !== null) {
    const start = match.index;
    const end = start + match[0].length;
    const prev = text[start - 1];
    // Structural: URL path segment or quoted attribute value.
    if (prev === '/' || prev === '"' || prev === "'") continue;
    // Longer hex/decimal token (session ids), not a standalone ObjectId.
    if (_HEX_CHAR.test(prev ?? '') || _HEX_CHAR.test(text[end] ?? '')) continue;

    let from = start;
    let to = end;
    if (prev === '`') from -= 1;
    if (text[to] === '`') to += 1;

    from = expandLeadingClausePunct(text, expandMachineIdLabelPrefix(text, from));
    removals.push({ from, to });
  }

  if (removals.length === 0) return trimEnd ? text.trimEnd() : text;

  let out = '';
  let cursor = 0;
  for (const { from, to } of removals) {
    if (from < cursor) continue; // overlapping removals
    out += text.slice(cursor, from);
    cursor = to;
  }
  out += text.slice(cursor);

  const cleaned = out
    .replace(/\(\s*\)/g, '')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\s+([.,;:])/g, '$1');
  return trimEnd ? cleaned.trimEnd() : cleaned;
};

export const stripMachineIds = (text: string | undefined): string => {
  if (!text || !/[a-f0-9]{24}/i.test(text)) return text ?? '';
  return rewriteOutsideMarkdownFences(text, stripMachineIdsFromProse);
};

/**
 * Keep children of project cite tags (titles) and drop the markup — for copy
 * clipboard and any path that should not retain citation tags.
 */
export const stripChatTags = (text: string, opts?: { streaming?: boolean }): string => {
  let out = text.replace(
    /<(?:project|chat)\b[^>]*>([\s\S]*?)<\/(?:project|chat)>/gi,
    (_match, children: string) => children
  );
  if (opts?.streaming) {
    out = stripIncompleteProjectCiteMarkup(out);
  }
  return out;
};

const shieldCiteTags = (
  text: string,
  opts?: { streaming?: boolean }
): { shielded: string; tags: string[] } => {
  const tags: string[] = [];
  let shielded = text.replace(_COMPLETE_CITE_TAG, (match) => {
    const index = tags.length;
    tags.push(match);
    return `\0CITETAG${index}\0`;
  });
  if (opts?.streaming) {
    // Drop dangling openers + tag-name prefixes so raw markup never paints.
    shielded = stripIncompleteProjectCiteMarkup(shielded);
  } else {
    // Incomplete tags cannot become chips — unwrap to title text (if any).
    shielded = unwrapDanglingProjectCiteMarkup(shielded);
  }
  return { shielded, tags };
};

const restoreCiteTags = (text: string, tags: string[]): string =>
  text.replace(
    _CITE_TAG_PLACEHOLDER,
    (_match, index: string) => tags[Number(index)] ?? ''
  );

/**
 * Models often emit ordered lists as ``1.\n**Title** body`` (marker alone
 * on its own line). GFM parses that as an empty ``<li>`` plus a sibling
 * paragraph, so the number and content render on separate lines. Collapse
 * the break so the item parses as a normal list entry.
 *
 * Require the following content to start with ``**`` so clinical prose like
 * ``140.\nPotassium normal.`` is left alone.
 */
const _BROKEN_ORDERED_LIST_MARKER = /^(\d{1,3})\.[ \t]*\n+(?=\*\*)/gm;

export const joinBrokenOrderedListMarkers = (text: string | undefined): string => {
  if (!text || !/\d\.[ \t]*\n/.test(text)) return text ?? '';
  return rewriteOutsideMarkdownFences(text, (prose) =>
    prose.replace(_BROKEN_ORDERED_LIST_MARKER, '$1. ')
  );
};

/**
 * Pre-Streamdown cleanup for work-agent assistant prose. `normalizeHeadings` is
 * the answer kit's outline normaliser, passed in so this module stays headless.
 */
export const prepareAssistantMarkdown = (
  text: string | undefined,
  normalizeHeadings: (markdown: string) => string,
  opts?: { streaming?: boolean }
): string => {
  if (!text) return '';
  const cleaned = stripTrustBoundaryTags(text);
  const normalized = prepareRefTagsForRender(cleaned, { streaming: opts?.streaming });
  // Cite and reference tags carry machine ids; shield complete tags so
  // ``stripMachineIds`` does not destroy them. While streaming, strip dangling
  // openers *and* tag-name prefixes (`<pro…`) so raw markup never paints.
  // Settled dangling openers unwrap to title text — never restored as raw HTML.
  const { shielded, tags } = shieldCiteTags(normalized, opts);
  // Heading levels last, so it sees the restored text and rewrites only what
  // Streamdown will actually parse as a heading.
  return normalizeHeadings(
    restoreCiteTags(joinBrokenOrderedListMarkers(stripMachineIds(shielded)), tags)
  );
};

export const friendlyToolError = (
  raw: string | undefined,
  infraErrorMessage = 'Heidi hit a temporary issue on this step. Please try again in a moment.'
): string => {
  const text = raw?.trim();
  if (!text) return 'Tool failed';
  const lower = text.toLowerCase();
  const looksLikeInfraNoise =
    /to bucket [a-z0-9-]+/.test(lower) ||
    lower.includes('s3uploaderror') ||
    lower.includes('expiredtoken') ||
    lower.includes('botocore') ||
    /\b(?:traceback|exception|errno)\b/.test(lower) ||
    /\.png|\.jpg|\.json|\.tmp/.test(lower);
  if (looksLikeInfraNoise) {
    return infraErrorMessage;
  }
  return text;
};
